import React, { useState } from 'react';
import { assignmentAPI } from '../utils/api';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const EditAmountModal = ({ assignment, onClose, onSaved }) => {
  const [mode, setMode] = useState(assignment.day_type || 'full_day');
  const [currency, setCurrency] = useState(
    (assignment.amount_inr > 0 && !(assignment.amount_usd > 0)) ? 'INR' : 'USD'
  );
  const [customUsd, setCustomUsd] = useState(assignment.amount_usd || 0);
  const [customInr, setCustomInr] = useState(assignment.amount_inr || 0);
  const [saving, setSaving] = useState(false);
  const paid = assignment.payment_status === 'paid';

  const handleSave = async () => {
    setSaving(true);
    try {
      await assignmentAPI.updateAmount(assignment.assignment_id, {
        mode,
        currency,
        custom_amount_usd: mode === 'custom' ? (parseFloat(customUsd) || 0) : null,
        custom_amount_inr: mode === 'custom' ? (parseFloat(customInr) || 0) : null,
      });
      toast.success('Amount updated');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update amount');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="edit-amount-modal">
      <div className="bg-background w-full max-w-md p-6 rounded" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-serif">Edit Amount</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-accent" data-testid="edit-amount-close"><X size={16} /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{assignment.crew_member_name}</p>

        {paid ? (
          <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-sm text-amber-800 dark:text-amber-300 rounded" data-testid="edit-amount-locked">
            This assignment is already marked <strong>PAID</strong>, so its amount is locked. Mark it unpaid first if you need to change it.
          </div>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-wide mb-2">Rate Type</label>
            <select
              value={mode}
              onChange={e => setMode(e.target.value)}
              className="w-full px-3 py-2 border border-input bg-background mb-4"
              data-testid="edit-amount-mode"
            >
              <option value="half_day">Half Day (standard rate)</option>
              <option value="full_day">Full Day (standard rate)</option>
              <option value="custom">Custom Amount</option>
            </select>

            {mode !== 'custom' && (
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-wide mb-2">Currency</label>
                <div className="flex gap-4">
                  {['USD', 'INR'].map(c => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={currency === c}
                        onChange={() => setCurrency(c)}
                        className="w-4 h-4 accent-gold"
                        data-testid={`edit-amount-currency-${c}`}
                      />
                      <span className="text-sm">{c === 'INR' ? 'INR (₹)' : 'USD ($)'}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Uses this crew member's saved {mode === 'half_day' ? 'half' : 'full'}-day rate.
                </p>
              </div>
            )}

            {mode === 'custom' && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Amount (USD)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={customUsd}
                    onChange={e => setCustomUsd(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background"
                    data-testid="edit-amount-custom-usd"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Amount (INR)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={customInr}
                    onChange={e => setCustomInr(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background"
                    data-testid="edit-amount-custom-inr"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary text-primary-foreground px-5 py-2.5 hover:bg-primary/90 disabled:opacity-50 text-sm"
                data-testid="edit-amount-save"
              >
                {saving ? 'Saving...' : 'Save Amount'}
              </button>
              <button onClick={onClose} className="border border-border px-5 py-2.5 hover:bg-accent text-sm" data-testid="edit-amount-cancel">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditAmountModal;
